import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

// Schema
import {
  AutoComplete,
  FormContainer,
  FormInput,
  IInputFile,
  ISelectType,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useState } from "react";
import ProtocolDefaultMediaFiles from "./ProtocolDefaultMediaFiles";
import {
  createProtocolDefault,
  getProtocolDefaultById,
  updateProtocolDefaultById,
} from "../../../../../../services/protocolDefault.service";
import {
  TProtocolDefaultForm,
  protocolDefaultSchema,
} from "./ProtocolDefaultSchema";
import ProtocolMessage from "./ProtocolMessage";

import MainApi from "../../../../../../services/mainApi.service";
import { TicketDefaultDocButtons } from "./TicketDefaultDocButtons";
import TicketDefaultDocCategoryField from "./TicketDefaultDocCategoryField";
import TicketDefaultDocRestrictionFields from "./TicketDefaultDocRestrictionFields";
import {
  buildRestrictionTypeOption,
  getRestrictionTypeValue,
  isChannelRestrictionType,
  isWorkspaceRestrictionType,
} from "./TicketDefaultDocRestrictionType";
const translationPath = "page.settings.crm.protocolsDefault.";

const TICKET_AUTOMATION_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TicketAutomation/";

const ticketAutomationService = new MainApi<ISelectType>(
  TICKET_AUTOMATION_ROUTE
);

const normalizeTicketDefaultDocCategory = (value: any) =>
  value
    ? {
        ...value,
        code: value.code != null ? String(value.code) : value.code,
      }
    : null;

const ProtocolDefault = () => {
  const { t } = useTranslation();

  const [ticketDefaultDocMediaFilesId, setTicketDefaultDocMediaFilesId] =
    useState<string[]>([]);

  let deleteFilesList: string[] = [];

  const onDeleFileHandler = (file: IInputFile) => {
    const selectedFile = file;
    if (selectedFile.id.length !== 0) {
      deleteFilesList.push(selectedFile.id);
    }
  };
  const [isCTAUrl, setIsCTAUrl] = useState(false);
  const [hasTicketDefaultDocButtonType, setHasTicketDefaultDocButtonType] =
    useState(false);

  const handleButtonTypeChange = useCallback((value: ISelectType | null) => {
    const code = value?.code ?? null;
    setIsCTAUrl(String(code ?? "") === "2");
    setHasTicketDefaultDocButtonType(!!value);
  }, []);

  const [hasTicketAutomation, setHasTicketAutomation] = useState(false);

  const handleTicketAutomationChange = useCallback(
    (value: ISelectType | null) => {
      setHasTicketAutomation(!!value);
    },
    []
  );

  const { sendRequest: save, loading: sLoading } = useFetch(
    createProtocolDefault
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateProtocolDefaultById
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getProtocolDefaultById
  );

  const handleGet = useCallback(
    async (id: string): Promise<Treturn<TProtocolDefaultForm>> => {
      const { data, success } = await get(id);
      if (data) {
        const filesId = data.ticketDefaultDocMediaFiles.map(
          (media) => media.id || ""
        );
        if (filesId) setTicketDefaultDocMediaFilesId(filesId);
        return {
          data: {
            ...data,
            ticketAutomation: data.ticketAutomation
              ? data.ticketAutomation
              : null,
            ticketDefaultDocButtonType: data.ticketDefaultDocButtonType
              ? data.ticketDefaultDocButtonType
              : null,
            restrictionType: buildRestrictionTypeOption(
              (data as any).restrictionType
            ),
            ticketDefaultDocCategory: normalizeTicketDefaultDocCategory(
              (data as any).ticketDefaultDocCategory
            ),
            ticketWorkspaces: data.ticketsWorkspaces,
            ticketChannels:
              (data as any).ticketsChannels ??
              (data as any).ticketChannels ??
              [],
            ticketDefaultDocMediaFiles: data.ticketDefaultDocMediaFiles.map(
              (media) => ({
                file: {
                  ...media,
                },
                description: media.description,
              })
            ),
            ticketDefaultDocButtons: data.ticketDefaultDocButtons
              ? data.ticketDefaultDocButtons.map((item: any) => ({
                  id: item.id ?? undefined,
                  label: item.label ?? "",
                  value: item.value ? item.value : undefined,
                }))
              : null,
          },
          success: true,
        };
      }
      return { data, success };
    },
    [get]
  );

  const handleSave = (v: TProtocolDefaultForm) => {
    return save({
      ...v,
      ticketDefaultDocMediaFiles: v.ticketDefaultDocMediaFiles
        ? v.ticketDefaultDocMediaFiles.map((media) => ({
            mediaFileId: media.file.id,
            description: media.description,
          }))
        : undefined,
      ticketAutomationId: v.ticketAutomation
        ? v.ticketAutomation.id
        : undefined,
      restrictionType: getRestrictionTypeValue(v.restrictionType),
      ticketDefaultDocCategoryId: v.ticketDefaultDocCategory
        ? v.ticketDefaultDocCategory.id
        : undefined,
      deleteMediaFilesIds: deleteFilesList,
      ticketWorkspacesId: isWorkspaceRestrictionType(v.restrictionType)
        ? (v.ticketWorkspaces ?? []).map((workspace) => workspace.id)
        : [],
      ticketChannelsId: isChannelRestrictionType(v.restrictionType)
        ? (v.ticketChannels ?? []).map((channel) => channel.id)
        : [],
      ticketDefaultDocButtons: v.ticketDefaultDocButtons
        ? v.ticketDefaultDocButtons.map((b) => ({
            label: b.label,
            value: b.value ? b.value : undefined,
            ticketDefaultDocButtonTypeId: v.ticketDefaultDocButtonType
              ? v.ticketDefaultDocButtonType?.id
              : "",
          }))
        : undefined,
    });
  };

  const handleUpdate = (v: TProtocolDefaultForm, id: string) => {
    const updatedFiles = v.ticketDefaultDocMediaFiles
      ? v.ticketDefaultDocMediaFiles.filter((media) =>
          ticketDefaultDocMediaFilesId.includes(media.file.id)
        )
      : undefined;
    const newFiles = v.ticketDefaultDocMediaFiles
      ? v.ticketDefaultDocMediaFiles.filter(
          (media) => !ticketDefaultDocMediaFilesId.includes(media.file.id)
        )
      : undefined;

    return update({
      item: {
        ...v,
        id,
        ticketAutomationId: v.ticketAutomation
          ? v.ticketAutomation.id
          : undefined,
        restrictionType: getRestrictionTypeValue(v.restrictionType),
        ticketDefaultDocCategoryId: v.ticketDefaultDocCategory
          ? v.ticketDefaultDocCategory.id
          : undefined,
        ticketDefaultMediaFilesToUpdate: updatedFiles
          ? updatedFiles.map((media) => ({
              id: media.file.id,
              description: media.description,
            }))
          : undefined,
        ticketDefaultDocMediaFiles: newFiles
          ? newFiles.map((media) => ({
              mediaFileId: media.file.id,
              description: media.description,
            }))
          : undefined,
        deleteMediaFilesIds: deleteFilesList,
        ticketWorkspacesId: isWorkspaceRestrictionType(v.restrictionType)
          ? (v.ticketWorkspaces ?? []).map((workspace) => workspace.id)
          : [],
        ticketChannelsId: isChannelRestrictionType(v.restrictionType)
          ? (v.ticketChannels ?? []).map((channel) => channel.id)
          : [],
        ticketDefaultDocButtons: v.ticketDefaultDocButtons
          ? v.ticketDefaultDocButtons.map((b) => ({
              id: b.id ? b.id : undefined,
              label: b.label,
              value: b.value ? b.value : undefined,
              ticketDefaultDocButtonTypeId: v.ticketDefaultDocButtonType
                ? v.ticketDefaultDocButtonType?.id
                : "",
            }))
          : undefined,
      },
      id: id,
    });
  };

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TProtocolDefaultForm>
      saveFunction={handleSave}
      updateFunction={handleUpdate}
      getFunction={handleGet}
      loading={loading}
      title={t(translationPath + "protocol_default")}
      schema={protocolDefaultSchema}
      subtitle={t(translationPath + "new_protocol_default")}
      subtitleWatchField={"title"}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} textAlign={"end"}>
            <SwitchInput name="enable" label={t(translationPath + "enable")} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="title"
              label={t(translationPath + "title")}
              inputProps={{
                maxLength: 1000,
              }}
              fullWidth
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              name="ticketAutomation"
              label={
                t(translationPath + "ticket_automation") || "Ticket Automation"
              }
              getOptionLabel={(option) => option.name}
              size="small"
              fullWidth
              clearIcon={true}
              request={ticketAutomationService.list}
              params={{ Enable: true }}
              onValueChange={handleTicketAutomationChange}
            />
          </Grid>

          <ProtocolMessage />

          <TicketDefaultDocRestrictionFields />

          <TicketDefaultDocCategoryField />

          <ProtocolDefaultMediaFiles onDeleteFileHandler={onDeleFileHandler} />

          <TicketDefaultDocButtons />

          {/* respiro para o último campo não ficar sob a barra Salvar/Cancelar */}
          <Grid item xs={12} sx={{ height: 80 }} />
        </Grid>
      )}
    />
  );
};

export default ProtocolDefault;
